"use client";

import { useRef } from "react";
import { BUTTON_SMALL, HINT } from "@/components/tool-kit/styles";
import {
  PhysicsPlot,
  PLOT_AREA,
} from "@/components/tools/physics-mechanics/plot";
import { t } from "@/lib/i18n/interpolate";
import { downloadPng } from "@/lib/tool-kit/download";
import { equalizeAspect } from "@/lib/tool-kit/math/plot";
import { CHART_COLORS } from "@/lib/tool-kit/palette";
import {
  formatPlain,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-mechanics/format";
import {
  type ProjectileInput,
  type ProjectileResult,
  trajectory,
} from "@/lib/tool-kit/physics-mechanics/projectile";

const RAD_TO_DEG = 180 / Math.PI;

export interface ProjectileStrings {
  graphTitle: string;
  xAxis: string;
  yAxis: string;
  launchPoint: string;
  peakPoint: string;
  landingPoint: string;
  downloadPng: string;
  graphNote: string;
  stepInputs: string;
  stepComponents: string;
  stepFlight: string;
  stepRange: string;
  stepPeak: string;
  stepNoPeak: string;
  stepImpact: string;
}

/** A negative number in brackets so a substituted minus sign cannot read as subtraction. */
function bracket(text: string): string {
  return text.startsWith("-") || text.startsWith("−") ? `(${text})` : text;
}

/** The numbered working, in SI units and degrees. */
export function projectileSteps(
  input: ProjectileInput,
  result: ProjectileResult,
  strings: ProjectileStrings,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const p = (value: number) => formatPlain(fmt, value);
  const r = (value: number) => formatSig(fmt, value, sig);
  const degrees = input.angle * RAD_TO_DEG;
  const vy = bracket(p(result.vy0));
  const steps = [
    t(strings.stepInputs, {
      v: p(input.speed),
      angle: p(degrees),
      h: p(input.height),
      g: p(input.gravity),
    }),
    t(strings.stepComponents, {
      v: p(input.speed),
      angle: p(degrees),
      vx: r(result.vx),
      vy: r(result.vy0),
    }),
    t(strings.stepFlight, {
      vy,
      g: p(input.gravity),
      h: p(input.height),
      time: r(result.timeOfFlight),
    }),
    t(strings.stepRange, {
      vx: r(result.vx),
      time: r(result.timeOfFlight),
      range: r(result.range),
    }),
  ];
  if (result.vy0 > 0) {
    steps.push(
      t(strings.stepPeak, {
        vy,
        g: p(input.gravity),
        h: p(input.height),
        time: r(result.peakTime),
        top: r(result.maxHeight),
      })
    );
  } else {
    steps.push(t(strings.stepNoPeak, { h: p(input.height) }));
  }
  steps.push(
    t(strings.stepImpact, {
      vy,
      g: p(input.gravity),
      time: r(result.timeOfFlight),
      vyf: r(result.impactVy),
      vx: r(result.vx),
      speed: r(result.impactSpeed),
      angle: r(result.impactAngle * RAD_TO_DEG),
    })
  );
  return steps;
}

export function TrajectoryGraph({
  input,
  result,
  strings,
  fmt,
  sig,
  lengthUnit,
  lengthFactor,
}: {
  input: ProjectileInput;
  result: ProjectileResult;
  strings: ProjectileStrings;
  fmt: NumberFormatter;
  sig: number;
  /** The distance unit's symbol. */
  lengthUnit: string;
  /** Metres per display unit. */
  lengthFactor: number;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const show = (metres: number) => metres / lengthFactor;
  const label = (metres: number) =>
    `${formatSig(fmt, show(metres), sig)} ${lengthUnit}`;
  const points = trajectory(input, result).map((point) => ({
    x: show(point.x),
    y: show(point.y),
  }));
  const yMax = Math.max(show(result.maxHeight), 1e-9);
  // A vertical throw has no range; keep the window wide enough to see it.
  const xMax = Math.max(show(result.range), yMax * 0.5, 1e-9);
  const viewport = equalizeAspect(
    {
      xMin: -xMax * 0.06,
      xMax: xMax * 1.08,
      yMin: -yMax * 0.1,
      yMax: yMax * 1.2,
    },
    PLOT_AREA.width,
    PLOT_AREA.height
  );
  // Equalising may have added room below the ground; put all of it above the path.
  const ySpan = viewport.yMax - viewport.yMin;
  const yBottom = -ySpan * 0.05;
  return (
    <div className="space-y-3">
      <PhysicsPlot
        formatTick={(value, decimals) =>
          fmt(value, { maximumFractionDigits: decimals, useGrouping: false })
        }
        markers={[
          {
            x: 0,
            y: show(input.height),
            color: CHART_COLORS[1],
            label: strings.launchPoint,
          },
          {
            x: show(result.peakX),
            y: show(result.maxHeight),
            color: CHART_COLORS[2],
            label: t(strings.peakPoint, { value: label(result.maxHeight) }),
          },
          {
            x: show(result.range),
            y: 0,
            color: CHART_COLORS[3],
            label: t(strings.landingPoint, { value: label(result.range) }),
            below: false,
          },
        ]}
        series={[{ points, color: CHART_COLORS[0] }]}
        svgRef={ref}
        title={t(strings.graphTitle, {
          range: label(result.range),
          height: label(result.maxHeight),
        })}
        xLabel={t(strings.xAxis, { unit: lengthUnit })}
        xRange={{ min: viewport.xMin, max: viewport.xMax }}
        yLabel={t(strings.yAxis, { unit: lengthUnit })}
        yRange={{ min: yBottom, max: yBottom + ySpan }}
      />
      <p className={HINT}>{strings.graphNote}</p>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadPng("projectile-trajectory.png", ref.current).catch(
              () => undefined
            );
          }
        }}
        type="button"
      >
        {strings.downloadPng}
      </button>
    </div>
  );
}
